/**
 * Scheduled-send time helpers. Picker values are `YYYY-MM-DDTHH:mm` wall-clock
 * strings in SCHEDULE_TZ (datetime-local format); the API receives UTC ISO.
 */
import { isoToZonedInputValue, pmTimezoneAbbreviation, zonedInputValueToIso } from './pmEventTime';

export const SCHEDULE_TZ = 'America/New_York';
export const SCHEDULE_MIN_LEAD_MS = 2 * 60 * 1000;

const pad = (n) => String(n).padStart(2, '0');

/** Today's date in SCHEDULE_TZ as { y, m, d } (m is 1-based). */
function zonedToday(now) {
  const [datePart] = isoToZonedInputValue(now.toISOString(), SCHEDULE_TZ).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  return { y, m, d };
}

function addDaysValue({ y, m, d }, days, time) {
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}T${time}`;
}

export function tomorrowAt9Value(now = new Date()) {
  return addDaysValue(zonedToday(now), 1, '09:00');
}

/** Next Monday strictly after today (ET), 9:00 AM. */
export function nextMondayAt9Value(now = new Date()) {
  const today = zonedToday(now);
  const dow = new Date(Date.UTC(today.y, today.m - 1, today.d)).getUTCDay();
  const days = (8 - dow) % 7 || 7;
  return addDaysValue(today, days, '09:00');
}

export const SCHEDULE_PRESETS = [
  { id: 'tomorrow', label: 'Tomorrow 9 AM', value: tomorrowAt9Value },
  { id: 'monday', label: 'Monday 9 AM', value: nextMondayAt9Value },
];

/** Earliest allowed picker value (now + 2 min, rounded up to the minute), for the input's `min`. */
export function minScheduleValue(now = new Date()) {
  const t = new Date(Math.ceil((now.getTime() + SCHEDULE_MIN_LEAD_MS) / 60000) * 60000);
  return isoToZonedInputValue(t.toISOString(), SCHEDULE_TZ);
}

/** Picker value → { iso } or { error }. */
export function validateScheduleValue(value, now = new Date()) {
  const iso = zonedInputValueToIso(value, SCHEDULE_TZ);
  if (!iso) return { error: 'Pick a date and time.' };
  if (new Date(iso).getTime() - now.getTime() < SCHEDULE_MIN_LEAD_MS) {
    return { error: 'Pick a time at least 2 minutes from now.' };
  }
  return { iso };
}

export function scheduleTzLabel(iso) {
  const abbr = pmTimezoneAbbreviation(iso || new Date().toISOString(), SCHEDULE_TZ);
  return `Eastern Time${abbr ? ` (${abbr})` : ''}`;
}

/** "Tue, Oct 7, 9:00 AM EDT" */
export function formatScheduledEt(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  const text = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  return `${text} ${pmTimezoneAbbreviation(iso, SCHEDULE_TZ)}`.trim();
}

export function isoToScheduleValue(iso) {
  return isoToZonedInputValue(iso, SCHEDULE_TZ);
}
